import { Fragment, useCallback, useMemo, useState } from 'react';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { FiltersOperator } from 'system/components/operators/list';
import {
  OperatorsResponse,
  transformerOperatorsList,
} from 'system/components/operators/utils/transformer-operators-list';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { FiltersFormOperator, OperatorClient } from 'system/view-models/operator-list';
import { useFormik } from 'formik';
import { transformDataset } from 'system/utils/operators-transform-dataset';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesOperator } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { generatePath, useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { Operator } from 'shell/models/operator';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonOperatorCrumbs } from 'system/constants/common-operator-crumbs';
import { headCells, validationSchemaOperatorsFilter } from 'system/constants/operators-list';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import Table from 'shell/components/shared/table';
import { authsObj } from 'system/constants/operators-authorizations';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { Typography } from '@mui/material';
import { CsvExtraction } from 'shell/components/shared/csv-extraction';
import { ResponseCsv } from 'shell/models/response-csv';
import { genericTransformer } from 'shell/utils/generic-transformer';
import { base64ToBlob } from 'shell/utils/base64-to-blob';
import { downloadBlob } from 'front-office/utils/download-blob';
import { DateTime } from 'luxon';

const OperatorsPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<OperatorClient>>({
    element: null,
    open: false,
  });
  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<OperatorClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormOperator>();

  const [pageData, getOperators] = useHttpFetch<HttpResponseList<Operator>, OperatorsResponse>(
    `${__MANAGEMENT_API_URL__}/operatori`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: transformerOperatorsList,
    }
  );

  const deleteOperator = useHttpClient('', {
    options: {
      errorCatching: true,
      method: 'delete',
      loading: true,
      data: {},
    },
  });

  const onSubmit = useCallback(
    (formValues: FiltersFormOperator) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterOperatorForm.setFieldValue('page', 0);
      getOperators({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getOperators]
  );

  const filterOperatorForm = useFormik({
    initialValues: initialValuesOperator,
    validationSchema: validationSchemaOperatorsFilter,
    onSubmit,
  });
  useEffect(() => {
    filterOperatorForm.setValues(filterTable.values || initialValuesOperator);
    const queryString = transformDataset(filterTable.values || initialValuesOperator);
    getOperators({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  useEffect(() => {
    if (pageData.data) {
      filterOperatorForm.setFieldValue('page', (pageData.data as OperatorsResponse).pageInfo?.page);
      filterOperatorForm.setFieldValue(
        'rowsPerPage',
        (pageData.data as OperatorsResponse).pageInfo?.pageSize
      );
      filterOperatorForm.setFieldValue(
        'totalCount',
        (pageData.data as OperatorsResponse).pageInfo?.totalCount
      );
    }
  }, [pageData.data]);

  useEffect(() => {
    return () => filterTable.setValues(filterOperatorForm.values);
  }, [filterOperatorForm.values]);

  const count: number = useMemo(() => {
    return (pageData.data as OperatorsResponse)?.pageInfo?.totalCount || 0;
  }, [pageData.data]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<OperatorClient> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/operatori/dettaglio/:id', { id: cell.code || '' }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<OperatorClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/operatori/modifica/:id', {
            id: cell.code || '',
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<OperatorClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<OperatorClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...filterOperatorForm.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      getOperators({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getOperators, filterOperatorForm.values, filterOperatorForm.setValues]
  );

  const handleChangeRowsPerPage = useCallback(
    (newRowsPerPage) => {
      const newDataset = {
        ...filterOperatorForm.values,
        rowsPerPage: newRowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      getOperators({
        params: {},
        paramsSerializer: () => queryString,
      });
      filterOperatorForm.setValues(newDataset);
    },
    [getOperators, filterOperatorForm.values, filterOperatorForm.setValues]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterOperatorForm.values, orders };
      const queryString = transformDataset(newDataset);
      getOperators({
        params: {},
        paramsSerializer: () => queryString,
      });
      filterOperatorForm.setFieldValue('orders', orders);
    },
    [getOperators, filterOperatorForm.values, filterOperatorForm.setValues]
  );

  const onCloseDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onYesDeleteAlertModal = useCallback(() => {
    (
      deleteOperator({
        url: `${__MANAGEMENT_API_URL__}/operatori/${deleteAlertModal.element?.code}`,
      }) as ResponsePromise<void>
    ).then(() => {
      setDeleteAlertModal({ open: false, element: null });
      setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
    });
  }, [deleteOperator, setDeleteAlertModal, setDeleteSuccessModal, deleteAlertModal.element]);

  const onNoDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onCloseDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset({ ...filterOperatorForm.values, page: 0 });
    getOperators({
      params: {},
      paramsSerializer: () => queryString,
    });
    setDeleteSuccessModal({ open: false, element: null });
  }, [setDeleteSuccessModal, filterOperatorForm.values, getOperators]);

  const onOkDeleteSuccessModal = useCallback(() => {
    const queryString = transformDataset({ ...filterOperatorForm.values, page: 0 });
    getOperators({
      params: {},
      paramsSerializer: () => queryString,
    });
    setDeleteSuccessModal({ open: false, element: null });
  }, [setDeleteSuccessModal, filterOperatorForm.values, getOperators]);

  const getOperatorsCSV = useHttpClient<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>(
    `${__MANAGEMENT_API_URL__}/operatori/csv`,
    {
      transformer: genericTransformer,
    }
  );

  const toPrintCSV = useCallback(() => {
    const newDataset = filterOperatorForm.values;
    const queryString = transformDataset(newDataset);
    (
      getOperatorsCSV({
        params: {},
        paramsSerializer: () => queryString,
      }) as ResponsePromise<HttpResponse<ResponseCsv>, HttpResponse<ResponseCsv>>
    ).then((response) => {
      const responseCasted = response as HttpResponse<ResponseCsv>;
      const csv = responseCasted?.payload?.contenutoCsv;
      if (csv) {
        // download csv
        const blob = base64ToBlob(csv);
        downloadBlob(blob, `export-csv-${DateTime.now().toFormat('ddMMyyHHmmss')}.csv`);
      }
    });
  }, [filterOperatorForm.values, getOperatorsCSV]);

  return (
    <Fragment>
      <Breadcrumbs items={CommonOperatorCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo operatore"
        addBtnLabel="Aggiungi nuovo operatore"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/operatori/nuovo');
        }}
        title="Gestione Operatori"
      />
      <div className="pmi-my-3">
        <FiltersOperator filtersOperatorForm={filterOperatorForm} />
      </div>
      {pageData.data ? (
        (pageData.data as OperatorsResponse)?.operators?.length > 0 ? (
          <>
            <div className="pmi-my-3">
              <CsvExtraction onClick={toPrintCSV} />
            </div>
            <Table
              onSort={onSort}
              head={headCells}
              orders={filterOperatorForm.values.orders || {}}
              activeActions={auths}
              page={filterOperatorForm.values.page}
              rowsPerPage={filterOperatorForm.values.rowsPerPage}
              body={(pageData.data as OperatorsResponse)?.operators}
              onVisibilityPress={onVisibilityPress}
              onEditPress={onEditPress}
              onTrashPress={onTrashPress}
              count={count || 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ariaLabel="Lista Operatori"
              isServer
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Operatore"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Operatore"
        message={`La cancellazione dell'operatore ${deleteSuccessModal.element?.name} è avvenuta con successo.`}
        onClose={onCloseDeleteSuccessModal}
        onOk={onOkDeleteSuccessModal}
      />
    </Fragment>
  );
};

OperatorsPage.displayName = 'Operators page component';

export default OperatorsPage;
